import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, Briefcase, PowerOff, Eye, UserPlus, UserMinus, AlertCircle } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import JobStatusBadge, { JobPriorityBadge } from "@/components/JobStatusBadge";
import SearchInput from "@/components/SearchInput";
import { useWithBack } from "@/lib/nav";
import ExportDropdown from "@/components/reports/ExportDropdown";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EntityCombobox } from "@/components/ui/EntityCombobox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { formatSlotShort } from "@/lib/slot";

const STATUS_CHIPS = ["new", "scheduled", "in_progress", "completed", "closed"];

// Office day-to-day view: pre-select "active work queue" statuses on landing
// with no explicit `?status=` in the URL. Completed/Closed/Aborted are archive
// states the office rarely needs by default. An explicit `?status=all` (or the
// user manually clearing the chips) yields an empty Set and fetches everything.
const DEFAULT_STATUSES = ["new", "scheduled", "in_progress"];

function parseStatusParam(raw) {
  // null   → param absent  → apply default work-queue statuses
  // "all"  → explicit "show everything" → empty Set (no filter sent to API)
  // ""     → explicit "show everything" → empty Set
  // "a,b"  → parse csv → Set of valid chip values
  if (raw === null) return { statuses: new Set(DEFAULT_STATUSES), explicit: false };
  if (raw === "all" || raw === "") return { statuses: new Set(), explicit: true };
  const items = raw.split(",").map((s) => s.trim()).filter(Boolean);
  return { statuses: new Set(items), explicit: true };
}

export default function JobsList() {
  const withBackHref = useWithBack();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("job.write");
  const canExport = hasPermission("report.export");

  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  // Seed from URL. Absent `?status=` → office default. `?status=all` → empty.
  const [statuses, setStatuses] = useState(() => parseStatusParam(searchParams.get("status")).statuses);
  const [engineerFilter, setEngineerFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [engineers, setEngineers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const reqIdRef = useRef(0);

  useEffect(() => {
    Promise.all([
      api.get("/jobs/engineers"),
      api.get("/customers", { params: { active: true, limit: 500 } }),
    ]).then(([{ data: e }, { data: c }]) => {
      setEngineers(e);
      setCustomers(c.items);
    }).catch((err) => toast.error(formatApiError(err)));
  }, []);

  useEffect(() => {
    if (customerFilter === "all") { setSites([]); setSiteFilter("all"); return; }
    api.get("/sites", { params: { customer_id: customerFilter, active: true, limit: 500 } })
      .then(({ data }) => setSites(data.items))
      .catch((err) => toast.error(formatApiError(err)));
  }, [customerFilter]);

  const load = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (search) params.search = search;
      if (showInactive) params.active = "all";
      if (statuses.size) params.status = Array.from(statuses).join(",");
      if (engineerFilter !== "all") params.engineer_id = engineerFilter;
      if (customerFilter !== "all") params.customer_id = customerFilter;
      if (siteFilter !== "all") params.site_id = siteFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/jobs", { params });
      if (myReqId !== reqIdRef.current) return;
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      toast.error(formatApiError(err));
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [search, showInactive, statuses, engineerFilter, customerFilter, siteFilter, dateFrom, dateTo]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  // Sync `statuses` ↔ `?status=` in the URL. Empty Set → `?status=all` sentinel
  // so a refresh doesn't reapply the default; non-empty → csv list.
  useEffect(() => {
    const desired = statuses.size === 0 ? "all" : Array.from(statuses).join(",");
    if (searchParams.get("status") === desired) return;
    const next = new URLSearchParams(searchParams);
    next.set("status", desired);
    setSearchParams(next, { replace: true });
  }, [statuses, searchParams, setSearchParams]);

  const engineerMap = useMemo(() => Object.fromEntries(engineers.map((e) => [e.id, e.full_name || e.username])), [engineers]);

  const toggleStatus = (s) => setStatuses((prev) => {
    const next = new Set(prev);
    if (next.has(s)) next.delete(s); else next.add(s);
    return next;
  });

  const handleUnassign = async (row) => {
    try { await api.post(`/jobs/${row.id}/unassign`); toast.success(`${row.job_number} unassigned`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const handleDeactivate = async (row) => {
    try { await api.delete(`/jobs/${row.id}`); toast.success(`${row.job_number} deactivated`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="jobs-page">
      <PageHeader
        testId="jobs-header"
        title="Jobs"
        subtitle={`${total} job${total === 1 ? "" : "s"} in view.`}
        actions={
          <div className="flex items-center gap-2">
            {canExport && (
              <ExportDropdown
                basePath="/jobs/export"
                params={(() => {
                  const p = {};
                  if (search) p.search = search;
                  if (showInactive) p.active = "all";
                  if (statuses.size) p.status = Array.from(statuses).join(",");
                  if (engineerFilter !== "all") p.engineer_id = engineerFilter;
                  if (customerFilter !== "all") p.customer_id = customerFilter;
                  if (siteFilter !== "all") p.site_id = siteFilter;
                  if (dateFrom) p.date_from = dateFrom;
                  if (dateTo) p.date_to = dateTo;
                  return p;
                })()}
                filename="jobs"
                testId="jobs-export"
              />
            )}
            {canWrite && (
              <Button asChild size="sm" data-testid="job-new-button"><Link to="/jobs/new"><Plus className="h-4 w-4" /> New Job</Link></Button>
            )}
          </div>
        }
      />

      <Card className="rounded-md">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center flex-wrap gap-2" data-testid="jobs-status-chips">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mr-1">Status</span>
            {STATUS_CHIPS.map((s) => {
              const on = statuses.has(s);
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`jobs-status-chip-${s}`}
                  onClick={() => toggleStatus(s)}
                  className={[
                    "px-2 py-0.5 text-xs rounded-sm border transition-colors",
                    on ? "bg-primary text-primary-foreground border-primary"
                       : "bg-transparent border-border text-muted-foreground hover:bg-muted",
                  ].join(" ")}
                >
                  {{ new: "New", scheduled: "Scheduled", in_progress: "In Progress", completed: "Completed", closed: "Closed" }[s]}
                </button>
              );
            })}
            {statuses.size > 0 && (
              <button type="button" onClick={() => setStatuses(new Set())} className="text-xs text-muted-foreground hover:text-foreground underline ml-2" data-testid="jobs-status-clear">Clear</button>
            )}
          </div>

          <div className="flex items-center flex-wrap gap-3">
            <SearchInput value={search} onChange={setSearch} placeholder="Job#, title, customer, site…" testId="jobs-search" />
            <div className="w-44">
              <EntityCombobox
                items={engineers}
                value={engineerFilter === "all" ? "" : engineerFilter}
                onChange={(v) => setEngineerFilter(v || "all")}
                placeholder="All engineers"
                emptyMessage="No engineers match."
                getLabel={(e) => e.full_name || e.username}
                allowClear
                clearLabel="All engineers"
                testid="jobs-engineer-filter"
                triggerClassName="h-8 text-xs"
              />
            </div>
            <div className="w-48">
              <EntityCombobox
                items={customers}
                value={customerFilter === "all" ? "" : customerFilter}
                onChange={(v) => setCustomerFilter(v || "all")}
                placeholder="All customers"
                emptyMessage="No customers match."
                allowClear
                clearLabel="All customers"
                testid="jobs-customer-filter"
                triggerClassName="h-8 text-xs"
              />
            </div>
            {customerFilter !== "all" && (
              <div className="w-48">
                <EntityCombobox
                  items={sites}
                  value={siteFilter === "all" ? "" : siteFilter}
                  onChange={(v) => setSiteFilter(v || "all")}
                  placeholder="All sites"
                  emptyMessage="No sites match."
                  allowClear
                  clearLabel="All sites"
                  testid="jobs-site-filter"
                  triggerClassName="h-8 text-xs"
                />
              </div>
            )}
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">From</span>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-36 text-xs" data-testid="jobs-date-from" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground ml-2">To</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-36 text-xs" data-testid="jobs-date-to" />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Show inactive</Label>
              <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="jobs-show-inactive" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-md overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="jobs-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-3">Job #</th>
                <th className="text-left py-2 px-3">Title</th>
                <th className="text-left py-2 px-3">Customer</th>
                <th className="text-left py-2 px-3">Site</th>
                <th className="text-left py-2 px-3">Machines</th>
                <th className="text-left py-2 px-3">Engineer</th>
                <th className="text-left py-2 px-3">Scheduled</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Priority</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={10} className="py-8 text-center text-xs text-muted-foreground" data-testid="jobs-empty">No jobs match these filters.</td></tr>
              ) : items.map((j) => {
                const first = j.scheduled_slots?.[0];
                const more = (j.scheduled_slots?.length || 0) - 1;
                return (
                  <tr key={j.id} data-testid={`job-row-${j.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="py-2 px-3">
                      <Link to={withBackHref(`/jobs/${j.id}`)} className="font-mono text-xs font-medium hover:text-primary" data-testid={`job-link-${j.id}`}>{j.job_number}</Link>
                    </td>
                    <td className="py-2 px-3 flex items-center gap-1.5">
                      <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate max-w-[240px]">{j.title}</span>
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">{j.customer_name || "—"}</td>
                    <td className="py-2 px-3 text-muted-foreground">{j.site_name || "—"}</td>
                    <td className="py-2 px-3 text-muted-foreground" title={`${j.machine_ids?.length || 0} machine(s)`}>
                      {j.machine_ids?.length || 0}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">
                      {j.engineer_id ? (engineerMap[j.engineer_id] || "Assigned") : <span className="italic">Unassigned</span>}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground text-xs">
                      {first ? (
                        <>
                          {formatSlotShort(first)}
                          {more > 0 && <span className="ml-1 text-[10px] font-bold uppercase tracking-wider">+{more} more</span>}
                        </>
                      ) : "—"}
                    </td>
                    <td className="py-2 px-3"><JobStatusBadge status={j.status} testId={`job-status-${j.id}`} /></td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        <JobPriorityBadge priority={j.priority} />
                        {j.actions_required && (
                          <span title="Actions required" data-testid={`job-actions-required-${j.id}`}><AlertCircle className="h-3.5 w-3.5 text-amber-600" /></span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`job-actions-${j.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`job-view-${j.id}`}><Eye className="h-4 w-4 mr-2" /> View</DropdownMenuItem>
                          {canWrite && <DropdownMenuItem onClick={() => navigate(`/jobs/${j.id}/edit`)} data-testid={`job-edit-${j.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>}
                          {canWrite && ["new","scheduled"].includes(j.status) && (
                            <DropdownMenuItem onClick={() => navigate(`/jobs/${j.id}/edit#assign`)} data-testid={`job-assign-${j.id}`}><UserPlus className="h-4 w-4 mr-2" /> {j.engineer_id ? "Reassign" : "Assign"}</DropdownMenuItem>
                          )}
                          {canWrite && j.status === "scheduled" && (
                            <DropdownMenuItem onClick={() => handleUnassign(j)} data-testid={`job-unassign-${j.id}`}><UserMinus className="h-4 w-4 mr-2" /> Unassign</DropdownMenuItem>
                          )}
                          {canWrite && j.active && j.status !== "in_progress" && (<>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDeactivate(j)} data-testid={`job-deactivate-${j.id}`}><PowerOff className="h-4 w-4 mr-2" /> Deactivate</DropdownMenuItem>
                          </>)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
